import { useCallback, useEffect, useRef, useState } from 'react'
import { sessionStore } from '@features/auth/services/sessionStore'
import {
  createFolderEncrypted,
  deleteFolderById,
  loadFolderList,
  updateFolderEncrypted,
  type FolderFlowDeps,
} from '../services/folderFlow'
import { type Folder } from '../types'

/**
 * フォルダ一覧の state machine (T-D-Vault-Folder、Phase 4)。
 *
 * loading / ready / error 状態で管理。CRUD 操作は楽観的更新ではなく
 * 操作後に reload して state を最新化する。
 */

export type FolderListState =
  | { kind: 'loading' }
  | { kind: 'ready'; folders: readonly Folder[] }
  | { kind: 'error' }

export interface UseFoldersOptions {
  flowDeps?: FolderFlowDeps
}

export interface UseFolders {
  state: FolderListState
  folders: readonly Folder[]
  createFolder: (name: string) => Promise<void>
  updateFolder: (id: string, name: string) => Promise<void>
  deleteFolder: (id: string) => Promise<void>
  reload: () => Promise<void>
}

export const useFolders = (options: UseFoldersOptions = {}): UseFolders => {
  const [state, setState] = useState<FolderListState>({ kind: 'loading' })
  const mountedRef = useRef(true)

  const fetchOnce = useCallback(async (): Promise<void> => {
    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      if (mountedRef.current) setState({ kind: 'error' })

      return
    }

    try {
      const folders = await loadFolderList(session.symmetricKey, options.flowDeps)
      if (mountedRef.current) setState({ kind: 'ready', folders })
    } catch {
      if (mountedRef.current) setState({ kind: 'error' })
    }
  }, [options.flowDeps])

  useEffect(() => {
    mountedRef.current = true
    void fetchOnce()

    return () => {
      mountedRef.current = false
    }
  }, [fetchOnce])

  const createFolder = useCallback(
    async (name: string): Promise<void> => {
      const session = sessionStore.getState()
      if (session.kind !== 'unlocked') return
      await createFolderEncrypted(session.symmetricKey, name, options.flowDeps)
      await fetchOnce()
    },
    [fetchOnce, options.flowDeps],
  )

  const updateFolder = useCallback(
    async (id: string, name: string): Promise<void> => {
      const session = sessionStore.getState()
      if (session.kind !== 'unlocked') return
      await updateFolderEncrypted(session.symmetricKey, id, name, options.flowDeps)
      await fetchOnce()
    },
    [fetchOnce, options.flowDeps],
  )

  const deleteFolder = useCallback(
    async (id: string): Promise<void> => {
      await deleteFolderById(id, options.flowDeps)
      await fetchOnce()
    },
    [fetchOnce, options.flowDeps],
  )

  const folders = state.kind === 'ready' ? state.folders : []

  return { state, folders, createFolder, updateFolder, deleteFolder, reload: fetchOnce }
}
